import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { differenceInDays, format, parseISO } from 'date-fns';
import api from '../config/api';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { SingleImageUploader } from '../components/ImageUploader';
import PartnerPlanWall from '../components/PartnerPlanWall';
import { DOCUMENT, documentFormLabelSx, documentOutlineButtonSx, documentSubSectionTitleSx } from '../constants/documentTheme';
import { partnerPageInnerSx, partnerPageWrapSx } from '../components/PartnerListChrome';
import { CURRENCIES } from '../utils/currencyUtils';
import { isPartnerPlanLimitError, canCreatePartnerGroupTrips, isBespokePartnerPlan } from '../utils/partnerPlan';

const parseDateParam = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isValidTripDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
};

const TRIP_TYPE_VALUES = ['leisure', 'conference', 'business', 'celebration', 'adventure'];

const underlineSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputBase-root': { pl: 0, pr: 0, width: '100%' },
  '& .MuiInputBase-input': {
    fontSize: 16,
    py: 1.25,
    pl: 0,
    pr: 0,
    color: DOCUMENT.ink,
    lineHeight: 1.5,
    overflow: 'visible',
  },
  '& .MuiInputBase-input::placeholder': { color: DOCUMENT.placeholder, opacity: 1 },
};

const CreateTrip = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { date } = useParams();
  const [searchParams] = useSearchParams();
  const partnerToken = localStorage.getItem('partnerToken');
  const isPartner = !!partnerToken;

  const initialMode = isPartner && searchParams.get('mode') === 'group' ? 'group' : 'client';
  const [mode, setMode] = useState(isPartner ? initialMode : 'personal');
  const fromPackageId = isPartner ? searchParams.get('fromPackage') : null;

  useEffect(() => {
    if (isPartner && searchParams.get('mode') === 'package') {
      navigate('/partner/packages/create', { replace: true });
    }
  }, [isPartner, navigate, searchParams]);

  useEffect(() => {
    if (!isPartner || !fromPackageId || !partnerToken) return undefined;
    let cancelled = false;
    const loadPackage = async () => {
      try {
        const response = await api.get(`/partner-packages/packages/${fromPackageId}`, {
          headers: { Authorization: `Bearer ${partnerToken}` },
        });
        const pkg = response.data?.package || response.data;
        if (cancelled || !pkg) return;
        const durationDays = Math.max(1, Number(pkg.duration_days) || 1);
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() + 14);
        const end = new Date(start);
        end.setDate(end.getDate() + durationDays - 1);
        const dest = pkg.destination || '';
        setFormData((prev) => ({
          ...prev,
          title: pkg.title || prev.title,
          description: pkg.description || prev.description,
          locations: dest && !prev.locations.some((loc) => loc.name.trim())
            ? [{ name: dest, coordinates: null }]
            : prev.locations,
          startDate: prev.startDate || format(start, 'yyyy-MM-dd'),
          endDate: prev.endDate || format(end, 'yyyy-MM-dd'),
          image_url: pkg.cover_image_url || pkg.image_url || prev.image_url,
        }));
        setOptionalOpen(true);
      } catch (err) {
        console.error('Failed to load package for customize:', err);
      }
    };
    loadPackage();
    return () => { cancelled = true; };
  }, [fromPackageId, isPartner, partnerToken]);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locations: [{ name: '', coordinates: null }],
    startDate: '',
    endDate: '',
    tripType: '',
    budget: '',
    budget_currency: 'USD',
    image_url: '',
    clientEmail: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planEntitlements, setPlanEntitlements] = useState(null);
  const [planWallReason, setPlanWallReason] = useState('client');
  const [sourceInquiryId, setSourceInquiryId] = useState(null);
  const [pendingAiSeed, setPendingAiSeed] = useState('');

  const canCreateGroups = canCreatePartnerGroupTrips(planEntitlements);
  const isBespoke = isBespokePartnerPlan(planEntitlements);
  const customizingPackage = Boolean(fromPackageId);

  useEffect(() => {
    if (customizingPackage) setMode('client');
  }, [customizingPackage]);

  useEffect(() => {
    if (!isPartner) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/partners/plan', {
          headers: { Authorization: `Bearer ${partnerToken}` },
        });
        if (!cancelled && response.data?.entitlements) {
          setPlanEntitlements(response.data.entitlements);
        }
      } catch (err) {
        console.error('Error loading partner plan:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isPartner, partnerToken]);

  useEffect(() => {
    if (!isPartner || canCreateGroups !== false) return;
    if (mode === 'group' || searchParams.get('mode') === 'group') {
      setMode('client');
      setPlanWallReason('group');
      setPlanWallOpen(true);
    }
  }, [canCreateGroups, isPartner, mode, searchParams]);

  useEffect(() => {
    if (!date) return;
    const selectedDate = parseDateParam(date);
    if (selectedDate && isValidTripDate(selectedDate)) {
      const startDate = selectedDate.toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, startDate, endDate: startDate }));
    } else if (selectedDate && !isValidTripDate(selectedDate)) {
      setError(t('createTrip.pastDate'));
    }
  }, [date, t]);

  useEffect(() => {
    const clientEmail = searchParams.get('clientEmail');
    const inquiryId = searchParams.get('inquiryId');
    const titleParam = searchParams.get('title');
    const destination = searchParams.get('destination');
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const aiSeed = searchParams.get('aiSeed');

    if (!clientEmail && !inquiryId && !titleParam && !destination && !startDateParam && !aiSeed) return;

    setFormData((prev) => ({
      ...prev,
      ...(clientEmail ? { clientEmail: decodeURIComponent(clientEmail) } : {}),
      ...(titleParam ? { title: decodeURIComponent(titleParam) } : {}),
      ...(destination ? {
        locations: [{ name: decodeURIComponent(destination), coordinates: null }],
      } : {}),
      ...(startDateParam ? { startDate: startDateParam } : {}),
      ...(endDateParam ? { endDate: endDateParam } : {}),
    }));
    if (inquiryId && Number.isFinite(Number(inquiryId))) {
      setSourceInquiryId(Number(inquiryId));
    }
    if (aiSeed) {
      setPendingAiSeed(decodeURIComponent(aiSeed));
    }
    if (clientEmail || titleParam || destination) {
      setOptionalOpen(true);
    }
  }, [searchParams]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  }, []);

  const handleLocationChange = useCallback((index, newValue) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (i === index ? { ...loc, name: newValue } : loc)),
    }));
    setValidationErrors((prev) => (prev[`location_${index}`] ? { ...prev, [`location_${index}`]: '' } : prev));
  }, []);

  const handleLocationSelect = useCallback((index, placeData) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (
        i === index ? {
          name: placeData.address,
          coordinates: placeData.coordinates,
          placeId: placeData.placeId || null
        } : loc
      )),
    }));
  }, []);

  const addLocation = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      locations: [...prev.locations, { name: '', coordinates: null }],
    }));
  }, []);

  const removeLocation = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.length > 1 ? prev.locations.filter((_, i) => i !== index) : prev.locations,
    }));
  }, []);

  const filledLocations = formData.locations.filter((loc) => loc.name.trim());
  const nights = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return null;
    return differenceInDays(parseISO(formData.endDate), parseISO(formData.startDate));
  }, [formData.startDate, formData.endDate]);

  const remainingCount = useMemo(() => {
    if (mode === 'package') return null;
    let need = 0;
    if (!formData.title.trim()) need += 1;
    if (isPartner && !formData.clientEmail.trim()) need += 1;
    if (!filledLocations.length) need += 1;
    if (!formData.startDate || !formData.endDate || nights == null || nights < 0) need += 1;
    return need;
  }, [formData, filledLocations.length, isPartner, mode, nights]);

  const isFormValid = remainingCount === 0 && mode !== 'package';

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.title.trim()) errors.title = t('createTrip.titleRequired');
    if (formData.title.length > 100) errors.title = t('createTrip.titleTooLong');
    if (isPartner && mode !== 'package') {
      if (!formData.clientEmail.trim()) {
        errors.clientEmail = isBespoke ? t('createTrip.emailRequired') : t('createTrip.emailRequiredAgent');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
        errors.clientEmail = t('createTrip.emailInvalid');
      }
    }
    formData.locations.forEach((location, index) => {
      if (index === 0 && !location.name.trim()) errors[`location_${index}`] = t('createTrip.destinationRequired');
    });
    if (!formData.startDate) errors.startDate = t('createTrip.startRequired');
    if (!formData.endDate) errors.endDate = t('createTrip.endRequired');
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      errors.endDate = t('createTrip.endAfterStart');
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isBespoke, isPartner, mode, t]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (mode === 'package' || !validateForm()) return;

    setError(null);
    setSuccess('');
    setSaving(true);

    try {
      const locations = formData.locations.filter((loc) => loc.name.trim());
      const tripType = formData.tripType || 'vacation';

      if (isPartner && mode === 'group') {
        const created = await api.post('/partners/master-trips', {
          title: formData.title,
          description: formData.description,
          locations,
          startDate: formData.startDate,
          endDate: formData.endDate,
          tripType,
        }, {
          headers: { Authorization: `Bearer ${partnerToken}` },
        });
        const tripId = created.data.tripId;
        if (formData.clientEmail.trim() && tripId) {
          try {
            await api.post(`/partners/master-trips/${tripId}/travelers`, {
              email: formData.clientEmail.trim(),
            }, {
              headers: { Authorization: `Bearer ${partnerToken}` },
            });
          } catch (travelerErr) {
            console.error('Error adding lead traveller:', travelerErr);
          }
        }
        navigate(`/partner/master-trips/${tripId}`);
        return;
      }

      if (isPartner) {
        const tripData = {
          userEmail: formData.clientEmail,
          title: formData.title,
          description: formData.description,
          locations,
          startDate: formData.startDate,
          endDate: formData.endDate,
          tripType,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          budget_currency: formData.budget_currency,
        };
        if (formData.image_url.trim()) tripData.image_url = formData.image_url.trim();
        if (fromPackageId) tripData.sourcePackageId = Number(fromPackageId);
        if (sourceInquiryId) {
          tripData.inquiryId = sourceInquiryId;
          tripData.pipelineStage = 'planning';
        }
        const response = await api.post('/partners/trips/create', tripData, {
          headers: { Authorization: `Bearer ${partnerToken}` },
        });
        if (response.data?.tripId) {
          const qs = pendingAiSeed
            ? `?openAi=1&aiSeed=${encodeURIComponent(pendingAiSeed)}`
            : '';
          navigate(`/partner/trips/${response.data.tripId}${qs}`);
          return;
        }
        if (response.data) {
          setSuccess(t('createTrip.success'));
          setTimeout(() => navigate('/partner/trips'), 2000);
        }
        return;
      }

      const tripData = {
        title: formData.title,
        description: formData.description,
        locations,
        start_date: formData.startDate,
        end_date: formData.endDate,
        trip_type: tripType,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        budget_currency: formData.budget_currency,
      };
      if (formData.image_url.trim()) tripData.image_url = formData.image_url.trim();
      const response = await api.post('/trips', tripData, {});
      if (response.data) {
        setSuccess(t('createTrip.success'));
        setTimeout(() => navigate('/trips'), 2000);
      }
    } catch (err) {
      if (isPartner && isPartnerPlanLimitError(err)) {
        setPlanEntitlements(err.response.data.entitlements || null);
        setPlanWallReason(mode === 'group' ? 'group' : 'client');
        setPlanWallOpen(true);
      }
      setError(err.response?.data?.error || t('createTrip.failed'));
      console.error('Error creating trip:', err);
    } finally {
      setSaving(false);
    }
  }, [formData, fromPackageId, isPartner, mode, navigate, partnerToken, pendingAiSeed, sourceInquiryId, t, validateForm]);

  const previewWhere = filledLocations.map((loc) => loc.name).join(' · ') || t('createTrip.noDestination');
  const previewDates = formData.startDate && formData.endDate && nights != null && nights >= 0
    ? `${format(parseISO(formData.startDate), 'd MMM yyyy')} – ${format(parseISO(formData.endDate), 'd MMM yyyy')}`
    : t('createTrip.notSet');
  const previewNights = nights == null || nights < 0
    ? '—'
    : t('createTrip.nightsCount', { count: nights });
  const nightsHint = nights == null
    ? ''
    : nights < 0
      ? t('createTrip.endAfterStart')
      : t('createTrip.nightsDays', { nights, days: nights + 1 });
  const previewType = mode === 'group'
    ? t('createTrip.modeGroup')
    : mode === 'package'
      ? t('createTrip.modePackage')
      : isPartner
        ? t(isBespoke ? 'createTrip.modeClient' : 'createTrip.modeClientAgent')
        : (formData.tripType ? t(`createTrip.types.${formData.tripType}`) : t('createTrip.personalTrip'));
  const previewClient = mode === 'package'
    ? t('createTrip.soldPublicly')
    : formData.clientEmail.trim() || t('createTrip.nobody');
  const coverLabel = filledLocations[0]?.name
    ? t('createTrip.coverPhoto', { dest: filledLocations[0].name })
    : t('createTrip.destinationPhoto');
  const cancelTo = customizingPackage
    ? '/partner/packages?customize=1'
    : isPartner ? '/partner/dashboard' : '/trips';

  return (
    <Box sx={isPartner ? partnerPageWrapSx : { bgcolor: DOCUMENT.canvas, minHeight: '100vh', color: DOCUMENT.ink }}>
      <Box sx={isPartner ? partnerPageInnerSx : {
        ...partnerPageInnerSx,
        pl: { xs: 2, sm: 3, md: 5 },
        pb: 10,
      }}>
        <Typography sx={{ pt: 4, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          <Box
            component={RouterLink}
            to={isPartner ? '/partner/dashboard' : '/trips'}
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: DOCUMENT.ink } }}
          >
            {t('createTrip.crumbOverview')}
          </Box>
          {' · '}
          {customizingPackage ? t('createTrip.customizeCrumb') : t('createTrip.crumbNew')}
        </Typography>
        <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 30, md: 42 }, fontWeight: 400, lineHeight: 1, mt: 1.5 }}>
          {customizingPackage ? t('createTrip.customizeTitle') : t('createTrip.title')}
        </Typography>

        {isPartner && customizingPackage && (
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink2, mt: 1.5, lineHeight: 1.6, maxWidth: '66ch' }}>
            {t('createTrip.customizeNote')}
          </Typography>
        )}

        {isPartner && !customizingPackage && (
          <>
            <Box
              role="group"
              aria-label={t('createTrip.tripKind')}
              data-testid="create-trip-kind"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                width: '100%',
                maxWidth: { sm: 560 },
                mt: 3.25,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                bgcolor: DOCUMENT.surface,
              }}
            >
              {[
                {
                  key: 'client',
                  label: t(isBespoke ? 'createTrip.modeClient' : 'createTrip.modeClientAgent'),
                  shortLabel: t('createTrip.modeClientShort'),
                },
                {
                  key: 'group',
                  label: t('createTrip.modeGroup'),
                  shortLabel: t('createTrip.modeGroupShort'),
                  locked: canCreateGroups === false,
                },
                {
                  key: 'package',
                  label: t('createTrip.modePackage'),
                  shortLabel: t('createTrip.modePackageShort'),
                },
              ].map((item, index, list) => {
                const active = mode === item.key;
                return (
                  <Box
                    key={item.key}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    aria-label={item.label}
                    onClick={() => {
                      if (item.key === 'package') {
                        navigate('/partner/packages/create');
                        return;
                      }
                      if (item.locked) {
                        setPlanWallReason('group');
                        setPlanWallOpen(true);
                        return;
                      }
                      setMode(item.key);
                    }}
                    sx={{
                      background: active ? DOCUMENT.oxblood : 'none',
                      color: active ? DOCUMENT.ivory : DOCUMENT.ink2,
                      border: 0,
                      borderRight: index < list.length - 1 ? `1px solid ${DOCUMENT.ruleHard}` : 0,
                      font: 'inherit',
                      fontSize: { xs: 10, sm: 11.5 },
                      letterSpacing: { xs: '0.06em', sm: '0.08em' },
                      textTransform: 'uppercase',
                      px: { xs: 0.75, sm: 1.25 },
                      py: 1.35,
                      cursor: 'pointer',
                      whiteSpace: 'normal',
                      minWidth: 0,
                      width: '100%',
                      lineHeight: 1.25,
                      textAlign: 'center',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      opacity: item.locked ? 0.65 : 1,
                    }}
                  >
                    <Box component="span" sx={{
                      display: { xs: 'none', sm: 'inline' },
                      overflowWrap: 'break-word',
                      minWidth: 0,
                    }}
                    >
                      {item.label}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{item.shortLabel}</Box>
                    {item.locked && <LockOutlinedIcon sx={{ fontSize: 13 }} aria-hidden />}
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{
              fontSize: 12.5,
              color: DOCUMENT.ink2,
              mt: 1.5,
              lineHeight: 1.6,
              maxWidth: '66ch',
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
            >
              {mode === 'client'
                ? t(isBespoke ? 'createTrip.noteClient' : 'createTrip.noteClientAgent')
                : t(`createTrip.note${mode.charAt(0).toUpperCase()}${mode.slice(1)}`)}
              {' '}
              {t('createTrip.emailReservationsNote')}
            </Typography>
          </>
        )}

        {!isPartner && !customizingPackage && (
          <Typography sx={{
            fontSize: 12.5,
            color: DOCUMENT.ink2,
            mt: 1.5,
            lineHeight: 1.6,
            maxWidth: '66ch',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
          }}
          >
            {t('createTrip.notePersonal')}
          </Typography>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 300px' },
            gap: { xs: 4, md: '52px' },
            mt: 4.5,
            alignItems: 'start',
          }}
        >
          <Box>
            <Box sx={{ mb: 4.25 }}>
              <Typography component="h2" sx={{ ...documentSubSectionTitleSx, pb: 1.35, borderBottom: `1px solid ${DOCUMENT.ruleHard}`, mb: 0.75 }}>
                {t('createTrip.essentials')}
                <Box component="span" sx={{ fontFamily: DOCUMENT.sans, fontSize: 12.5, color: DOCUMENT.ink3, ml: 1.5 }}>
                  {t('createTrip.essentialsSub')}
                </Box>
              </Typography>

              <Field label={t('createTrip.tripName')} required wide>
                <TextField
                  id="trip-title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="standard"
                  placeholder={t('createTrip.tripNamePlaceholder')}
                  error={!!validationErrors.title}
                  helperText={validationErrors.title || t(
                    isPartner && !isBespoke ? 'createTrip.tripNameHintAgent' : 'createTrip.tripNameHint'
                  )}
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ 'aria-label': t('createTrip.tripName') }}
                  sx={underlineSx}
                />
              </Field>

              {isPartner && mode !== 'package' && (
                <Field label={mode === 'group' ? t('createTrip.leadEmail') : t(isBespoke ? 'createTrip.clientEmail' : 'createTrip.clientEmailAgent')} required wide>
                  <TextField
                    id="client-email"
                    name="clientEmail"
                    type="email"
                    value={formData.clientEmail}
                    onChange={handleChange}
                    required
                    fullWidth
                    variant="standard"
                    placeholder="name@company.com"
                    error={!!validationErrors.clientEmail}
                    helperText={validationErrors.clientEmail || (mode === 'group'
                      ? t('createTrip.leadEmailHint')
                      : t(isBespoke ? 'createTrip.clientEmailHint' : 'createTrip.clientEmailHintAgent'))}
                    inputProps={{
                      'aria-label': mode === 'group'
                        ? t('createTrip.leadEmail')
                        : t(isBespoke ? 'createTrip.clientEmail' : 'createTrip.clientEmailAgent'),
                    }}
                    sx={underlineSx}
                  />
                </Field>
              )}

              <Field label={t('createTrip.destination')} required wide>
                {formData.locations.map((location, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: index < formData.locations.length - 1 ? 1.5 : 0, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                      <LocationAutocomplete
                        value={location.name}
                        onChange={(newValue) => handleLocationChange(index, newValue)}
                        onPlaceSelect={(placeData) => handleLocationSelect(index, placeData)}
                        label=""
                        required={index === 0}
                        fullWidth
                        variant="standard"
                        error={!!validationErrors[`location_${index}`]}
                        helperText={validationErrors[`location_${index}`]}
                        placeholder={t('createTrip.destinationPlaceholder')}
                        sx={underlineSx}
                      />
                    </Box>
                    {formData.locations.length > 1 && (
                      <Box component="button" type="button" onClick={() => removeLocation(index)} sx={ghostSx}>
                        {t('createTrip.remove')}
                      </Box>
                    )}
                  </Box>
                ))}
                <Box component="button" type="button" onClick={addLocation} sx={{ ...ghostSx, pt: 1.75 }}>
                  {t('createTrip.addDestination')}
                </Box>
              </Field>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25 }} data-testid="date-range-picker">
                <Field label={t('createTrip.startDate')} required>
                  <TextField
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    fullWidth
                    variant="standard"
                    error={!!validationErrors.startDate}
                    helperText={validationErrors.startDate}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'start-date', 'aria-label': t('createTrip.startDate') }}
                    sx={underlineSx}
                  />
                </Field>
                <Field label={t('createTrip.endDate')} required>
                  <TextField
                    name="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    fullWidth
                    variant="standard"
                    error={!!validationErrors.endDate}
                    helperText={validationErrors.endDate || nightsHint || ' '}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: formData.startDate, 'data-testid': 'end-date', 'aria-label': t('createTrip.endDate') }}
                    sx={{
                      ...underlineSx,
                      '& .MuiFormHelperText-root': { color: nights != null && nights >= 0 ? DOCUMENT.accent : DOCUMENT.ink3 },
                    }}
                  />
                </Field>
              </Box>
            </Box>

            <Box
              component="button"
              type="button"
              aria-expanded={optionalOpen}
              onClick={() => setOptionalOpen((open) => !open)}
              sx={{
                background: 'none',
                border: `1px solid ${DOCUMENT.ruleHard}`,
                font: 'inherit',
                fontSize: 11,
                letterSpacing: '0.11em',
                textTransform: 'uppercase',
                color: DOCUMENT.ink2,
                px: 2.25,
                py: 1.35,
                cursor: 'pointer',
                '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.ink },
              }}
            >
              {optionalOpen ? t('createTrip.hideOptional') : t('createTrip.optionalToggle')}
            </Box>

            {optionalOpen && (
              <Box sx={{ mt: 3.75 }}>
                <Typography component="h2" sx={{ ...documentSubSectionTitleSx, pb: 1.35, borderBottom: `1px solid ${DOCUMENT.ruleHard}`, mb: 0.75 }}>
                  {t('createTrip.optional')}
                  <Box component="span" sx={{ fontFamily: DOCUMENT.sans, fontSize: 12.5, color: DOCUMENT.ink3, ml: 1.5 }}>
                    {t('createTrip.optionalSub')}
                  </Box>
                </Typography>
                <Field label={t('createTrip.tripType')} wide>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.25 }}>
                    {TRIP_TYPE_VALUES.map((type) => {
                      const active = formData.tripType === type;
                      return (
                        <Box
                          key={type}
                          component="button"
                          type="button"
                          aria-pressed={active}
                          onClick={() => setFormData((prev) => ({ ...prev, tripType: active ? '' : type }))}
                          sx={{
                            background: active ? DOCUMENT.oxblood : 'none',
                            border: `1px solid ${active ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
                            color: active ? DOCUMENT.ivory : DOCUMENT.ink2,
                            font: 'inherit',
                            fontSize: 12.5,
                            px: 1.85,
                            py: 1,
                            cursor: 'pointer',
                          }}
                        >
                          {t(`createTrip.types.${type}`)}
                        </Box>
                      );
                    })}
                  </Box>
                </Field>
                <Field label={t('createTrip.description')} wide>
                  <TextField
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    variant="standard"
                    placeholder={t('createTrip.descriptionPlaceholder')}
                    inputProps={{ 'aria-label': t('createTrip.description') }}
                    sx={underlineSx}
                  />
                </Field>
                <Field label={t('createTrip.coverImage')} wide>
                  <SingleImageUploader
                    value={formData.image_url}
                    onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url || '' }))}
                    preset="cover"
                    folder="trips/covers"
                    label={t('createTrip.coverBrowse')}
                    height={140}
                    cropShape="rect"
                  />
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 1 }}>{t('createTrip.coverHint')}</Typography>
                </Field>
                <Field label={t('createTrip.budget')} wide>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 2.25 }}>
                    <TextField
                      name="budget"
                      type="number"
                      value={formData.budget}
                      onChange={handleChange}
                      fullWidth
                      variant="standard"
                      placeholder="0"
                      inputProps={{ min: 0, 'aria-label': t('createTrip.budget') }}
                      sx={underlineSx}
                    />
                    <TextField
                      select
                      name="budget_currency"
                      value={formData.budget_currency}
                      onChange={handleChange}
                      variant="standard"
                      sx={underlineSx}
                    >
                      {CURRENCIES.filter((c) => ['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(c.code)).concat([{ code: 'AED', name: 'UAE Dirham', symbol: 'AED' }]).map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>{currency.code}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 1 }}>{t('createTrip.budgetHint')}</Typography>
                </Field>
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}

            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                bgcolor: DOCUMENT.canvas,
                borderTop: `1px solid ${DOCUMENT.ruleHard}`,
                mt: 2.5,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              <Button
                type="button"
                onClick={() => navigate(cancelTo)}
                sx={{
                  ...documentOutlineButtonSx,
                  minWidth: 120,
                }}
              >
                {t('createTrip.cancel')}
              </Button>
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                {mode === 'package'
                  ? t('createTrip.packageSoon')
                  : remainingCount === 0
                    ? t('createTrip.ready')
                    : t(remainingCount === 1 ? 'createTrip.fieldRemaining' : 'createTrip.fieldsRemaining', { count: remainingCount })}
              </Typography>
              <Button
                type="submit"
                disabled={!isFormValid || saving}
                sx={{
                  ml: { sm: 'auto' },
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  borderRadius: 0,
                  px: 2.75,
                  py: 1.5,
                  fontSize: 11,
                  letterSpacing: '0.11em',
                  textTransform: 'uppercase',
                  '&:hover': { bgcolor: DOCUMENT.nav },
                  '&.Mui-disabled': { bgcolor: DOCUMENT.ruleHard, color: '#fff' },
                }}
              >
                {saving ? t('trip.modal.saving') : (isPartner ? t('createTrip.createBuilder') : t('createTrip.create'))}
              </Button>
            </Box>
          </Box>

          <Box
            data-testid="trip-preview"
            sx={{
              position: { md: 'sticky' },
              top: 26,
              border: `1px solid ${DOCUMENT.ruleHard}`,
              bgcolor: DOCUMENT.surface,
              order: { xs: -1, md: 0 },
            }}
          >
            <Box sx={{ px: 2.25, py: 1.5, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
              <Typography sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                {t('createTrip.preview')}
              </Typography>
            </Box>
            <Box sx={{ p: 2.25 }}>
              <Box
                sx={{
                  height: 104,
                  bgcolor: DOCUMENT.oxblood,
                  color: '#C9A961',
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 1.75,
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  backgroundImage: formData.image_url ? `url(${formData.image_url})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {!formData.image_url && coverLabel}
              </Box>
              <Typography
                data-testid="preview-name"
                sx={{
                  fontFamily: DOCUMENT.display,
                  fontSize: 23,
                  lineHeight: 1.15,
                  mt: 1.85,
                  mb: 0.75,
                  color: formData.title.trim() ? DOCUMENT.ink : DOCUMENT.placeholder,
                }}
              >
                {formData.title.trim() || t('createTrip.untitled')}
              </Typography>
              <Typography data-testid="preview-where" sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
                {previewWhere}
              </Typography>
              <Box sx={{ mt: 1.85, pt: 1.6, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                <PreviewRow label={t('createTrip.dates')} value={previewDates} muted={!formData.startDate || !formData.endDate || nights < 0} />
                <PreviewRow label={t('createTrip.duration')} value={previewNights} muted={nights == null || nights < 0} />
                <PreviewRow label={t('createTrip.type')} value={previewType} />
                {isPartner && (
                  <PreviewRow label={t('createTrip.sharedWith')} value={previewClient} muted={mode !== 'package' && !formData.clientEmail.trim()} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        entitlements={planEntitlements}
        reason={planWallReason}
      />
    </Box>
  );
};

const ghostSx = {
  background: 'none',
  border: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  cursor: 'pointer',
  p: 0,
  pb: '2px',
  borderBottom: '1px solid transparent',
  '&:hover': { color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
};

const Field = ({ label, required, wide, children }) => (
  <Box sx={{ py: 2.1, borderBottom: `1px solid ${DOCUMENT.rule}`, gridColumn: wide ? '1 / -1' : 'auto' }}>
    <Typography sx={documentFormLabelSx}>
      {label}
      {required && <Box component="span" sx={{ color: DOCUMENT.accent, ml: 0.4 }}>*</Box>}
    </Typography>
    {children}
  </Box>
);

const PreviewRow = ({ label, value, muted }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.6, fontSize: 12.5 }}>
    <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12.5 }}>{label}</Typography>
    <Typography sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right', fontSize: 12.5, color: muted ? DOCUMENT.placeholder : DOCUMENT.ink }}>
      {value}
    </Typography>
  </Box>
);

export default CreateTrip;
